import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PartnerDbModel } from '@db-models/partner-db.model';
import { WorkerDbModel } from '@db-models/worker-db.model';
import { environment } from '@environments/environment';
import { AuthService } from '@feature-services/auth.service';
import { FeatureService } from '@feature-services/feature.service';
import { PartnerService } from '@feature-services/partner.service';
import { ThemeService } from '@feature-services/theme.service';
import { ColorSchemeService } from '@util-services/color-scheme.service';
import { WorkerService } from '@feature-services/worker.service';
import { TranslateModule } from '@ngx-translate/core';
import { CalioEventsService } from '@util-services/calio-events.service';
import { HelperService } from '@util-services/helper.service';
import { IconService } from '@util-services/icon.service';
import { LoggerService } from '@util-services/logger.service';
import { NgProgressbar } from 'ngx-progressbar';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, NgProgressbar, TranslateModule]
})
export class AppComponent implements OnInit {

  private partnerService = inject(PartnerService);
  private router = inject(Router);
  private calioEventsService = inject(CalioEventsService);
  private workerService = inject(WorkerService);
  private authService = inject(AuthService);
  private featureService = inject(FeatureService);
  private destroyRef = inject(DestroyRef);
  private helperService = inject(HelperService);
  private msalAuthService = inject(MsalService);
  private iconService = inject(IconService);
  private themeService = inject(ThemeService);
  private colorSchemeService = inject(ColorSchemeService);

  protected readonly resellerWhitelistPaths = ['appointments', 'events', 'customers', 'settings'];

  protected lang: string;
  protected isIeorEdgeBrowser = false;
  protected loggedInWorker: WorkerDbModel;
  protected partner: PartnerDbModel;
  protected loggedIn = this.authService.hasValidAuthToken();

  constructor() {
    this.partnerService.setLangInLocalStorage(null, null);
  }

  ngOnInit(): void {
    // The SSO page asks for this same result again through the same call and relies on MSAL
    // remembering it under the same key. Keep it MsalService.handleRedirectObservable() with no
    // argument - instance.handleRedirectPromise() would make MSAL handle the redirect twice.
    this.msalAuthService.handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.themeService.loadTheme();
    this.iconService.registerAllIcons();
    this.loggedInWorker = this.workerService.getLoggedInWorkerFromLocalStorage();
    this.partner = this.partnerService.getPartnerFromLocalStorage();

    this.authService.userIsLoggedInEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(worker => {
        this.loggedInWorker = worker;
        // Server-side theme preference wins over the local no-flash cache so the
        // choice follows the worker across browsers/devices.
        this.colorSchemeService.hydrateFromWorker(worker?.theme_preference);
        this.partner = this.partnerService.getPartnerFromLocalStorage();
        if (this.partner?.subscription) {
          this.featureService.loadSubscriptionFeatures(this.partner.subscription);
          this.featureService.loadFeaturesMasterList();
        }
        this.appendGaTrackingCode();
      });

    this.authService.userLogOutEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loggedInWorker = undefined;
        this.partner = undefined;
      });

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd) // Filter NavigationEnd events
      )
      .subscribe((event: NavigationEnd) => {
        this.helperService.currentUrl = event.url;
        this.calioEventsService.routeChangedEvent$.emit(event);
      });

    if (this.loggedIn) {
      this.loggedInWorker = this.workerService.getLoggedInWorkerFromLocalStorage();
      // Reload while already signed in — re-seed from the stored worker payload.
      this.colorSchemeService.hydrateFromWorker(this.loggedInWorker?.theme_preference);
      this.featureService.initializeFeature();
      this.appendGaTrackingCode();
    }
  }

  private appendGaTrackingCode(): void {
    try {
      const script = document.createElement('script');
      script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${environment.googleTagManagerCode}');`;
      document.head.appendChild(script);
    } catch {
      LoggerService.error('Error appending Google Tag Manager');
    }
  }
}
